import { capacityQuantityFieldLabel } from './capacityPolysemy';
/**
 * Shared report text extraction for audit gates (R-67, R-79, CLI).
 */
import type { ReportTypeId } from '../../config/reportTypes';
import { getSectionLabel } from '../../config/reportSections';
import type { SiteRagReport } from '../../types/siteRag';
import type { GenerationImpactReportProjection } from '../../types/generationImpact';

/**
 * Flatten a report value to reviewer-visible text.
 *
 * STRUCTURE-PRESERVING for arrays of objects (2026-08-05): the previous
 * implementation dropped every KEY (`Object.values(...).join`), so a
 * structurally-correct `developmentPathway` — an array of
 * `{phase,title,description,capacityMW,timeline}` objects — reached the
 * reviewer as one undifferentiated run of prose. The reviewer then filed a
 * BLOCKING accuracy issue ("not formatted correctly as an array of phase
 * objects … rendered as a flat list") against data that was in fact perfectly
 * well-formed (live v1.22.1 Redhead run). That is a harness artifact
 * manufacturing a false defect — the same class as the epistemic risk-schema
 * misread. Row objects now render as `- key: value | key: value`, so the
 * reviewer judges the real shape. Strings, scalars and plain object values are
 * unchanged, so every other consumer (word counts, citation density,
 * distribution stats) sees the same text it did before, modulo the added
 * key labels on object rows.
 */
/**
 * True when a "sentence" is really a FLATTENED OBJECT, not prose.
 *
 * `flattenToText` (below) renders an array of objects as
 *   `- Key: value | Key: value | Key: value`
 * so that word counts and citation density measure the whole report. But the
 * sentence-level truth detectors then read those lines as assertions. Live on
 * 2026-08-05 that produced blocking findings on machine serialization:
 *
 *   absence  "- Id: dev-risk-fuel-supply | Label: Fuel Supply | Category: …"
 *   caveat   "- System: Wastewater | Total Capacity: Data not available | …"
 *
 * Worse, the SAME underlying claim was flagged twice, once through the
 * `developmentRisks` serialization and once through `risks`, because both
 * render the same prose into different records.
 *
 * A record is identified by its generated shape — two or more pipe-separated
 * segments that each open with a humanized `Key: `. Counting rather than
 * requiring EVERY segment to match keeps it robust when a value itself
 * contains a pipe. Ordinary prose does not take this form, so a detector that
 * skips these loses no real claim.
 */
export function isSerializedRecord(text: string): boolean {
  const raw = text.trim();
  // A leading pipe means the splitter cut INSIDE a record — no English prose
  // opens with `|`. Same reasoning as `isSentenceFragment`'s leading-clause
  // punctuation rule: it cannot begin a sentence, so it asserts nothing.
  if (raw.startsWith('|')) return true;
  if (/^doc:[^\s|]+\s*(?:,\s*(?:doc|ref|sed-section):[^\s|]+)*\s*\|\s*Text:\s/i.test(raw)) return true;
  const trimmed = raw.replace(/^-\s+/, '');
  const segments = trimmed.split(' | ');
  if (segments.length < 2) return false;
  const keyed = segments.filter((seg) => /^[A-Z][A-Za-z0-9 /()\-]{0,40}:\s/.test(seg)).length;
  return keyed >= 2;
}

export function flattenToText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) {
    return v
      .map((entry) => (
        entry && typeof entry === 'object' && !Array.isArray(entry)
          ? `- ${Object.entries(entry as Record<string, unknown>)
              .filter(([, val]) => val != null && val !== '')
              .map(([key, val]) => `${humanizeKey(key)}: ${flattenToText(val)}`)
              .join(' | ')}`
          : flattenToText(entry)
      ))
      .join('\n');
  }
  if (typeof v === 'object') return Object.entries(v as Record<string, unknown>).map(([key, value]) => {
    const label = capacityQuantityFieldLabel(key);
    const text = flattenToText(value);
    return label && (typeof value === 'string' || typeof value === 'number')
      ? `${label}: ${text}` : text;
  }).join('\n');
  return String(v);
}

/** camelCase / snake_case sub-key → Title Case label for an H3 sub-heading. */
function humanizeKey(k: string): string {
  return k
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Render an object-valued section as markdown with an H3 (`### {label}`) per
 * SUBSTANTIVE top-level sub-key (one whose flattened content is real prose, not
 * a bare scalar/flag), followed by that sub-key's flattened body. This mirrors
 * the structure a reader sees in an object-backed section (sub-cards / sub-blocks)
 * so the distribution gate measures real subsection structure instead of 0 H3.
 * Scalar / very short sub-values are inlined (no heading) so we don't inflate
 * H3 with metadata flags. (report-gate-rootcause-fixplan STEP 3/4.)
 */
const SUBHEADING_MIN_CHARS = 40;

function flattenFirmCapacityN1Fields(value: unknown): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return flattenToText(value);
  const labels: Record<string, string> = {
    largestUnitMW: 'Largest Contingency',
    firmDeliverableN1MW: 'Firm Deliverable Under N-1',
    requiredFirmMW: 'Required Firm Capacity',
    shortfallMW: 'Shortfall',
  };
  return Object.entries(value as Record<string, unknown>)
    .filter(([, fieldValue]) => fieldValue != null && fieldValue !== '')
    .map(([field, fieldValue]) => `${labels[field] ?? humanizeKey(field)}: ${flattenToText(fieldValue)}`)
    .join('\n');
}

function renderObjectWithSubheadings(obj: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    // The N-1 object contains several distinct MW roles side-by-side. Preserve
    // its child field names so grounding can distinguish remaining firm MW
    // from required firm MW, the contingency, and the shortfall. A values-only
    // dump made model-authored arithmetic impossible to attribute safely.
    const body = (k === 'firmCapacityN1'
      ? flattenFirmCapacityN1Fields(v)
      : flattenToText(v)).trim();
    if (!body) continue;
    if (body.length >= SUBHEADING_MIN_CHARS) parts.push(`### ${humanizeKey(k)}\n${body}`);
    else parts.push(body);
  }
  return parts.join('\n\n');
}

/**
 * C0-GEN Wave 2 (spec fix #1) — flatten the generated `editorialNarrative`
 * layer into markdown prose so the audit gates (R-66 citation integrity,
 * R-67 internal numbers, R-72 distribution) and the fact-check SEE the
 * editorial re-voicing. Without this, the editorial prose would be a quietly
 * unchecked text surface — the trust-critic's #1 FAIL on the naïve design.
 *
 * Each per-section layer becomes a `### {sectionId}` sub-block carrying its
 * headline + thesis + narrative + pull-quote text. The metadata fields
 * (`generatedAt`, `modelUsed`) are deliberately NOT flattened — they carry no
 * citations and would only add noise. Returns `''` when no layer is present
 * (flag-OFF / legacy reports), so the audit body stays byte-identical to today.
 */
export function flattenEditorialNarrative(report: SiteRagReport): string {
  const en = report.editorialNarrative;
  if (!en || typeof en !== 'object' || !en.sections) return '';
  const blocks: string[] = [];
  for (const [sectionId, layer] of Object.entries(en.sections)) {
    if (!layer || typeof layer !== 'object') continue;
    const parts: string[] = [];
    if (typeof layer.editorialHeadline === 'string' && layer.editorialHeadline.trim()) {
      parts.push(layer.editorialHeadline.trim());
    }
    if (typeof layer.editorialThesis === 'string' && layer.editorialThesis.trim()) {
      parts.push(layer.editorialThesis.trim());
    }
    if (typeof layer.editorialNarrative === 'string' && layer.editorialNarrative.trim()) {
      parts.push(layer.editorialNarrative.trim());
    }
    const pq = layer.pullQuote?.text;
    if (typeof pq === 'string' && pq.trim()) parts.push(pq.trim());
    if (parts.length > 0) blocks.push(`### ${sectionId}\n${parts.join('\n\n')}`);
  }
  return blocks.join('\n\n');
}

const fmtInt = (value: number | null | undefined): string =>
  value == null || !Number.isFinite(value)
    ? 'Indeterminate'
    : value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct1 = (value: number): string =>
  value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const engineeringVerdictLabel = (verdict: string): string =>
  verdict === 'pass' ? 'Pass' : verdict === 'fail' ? 'Shortfall' : 'Indeterminate';

/**
 * P3.3 (2026-09-05) — render the frozen promoted-scenario duty tables
 * (`report.generationImpactProjection`) exactly as `GenerationWaterBlock`,
 * `GenerationFuelSupplyBlock` and `GenerationFootprintBlock` show them: same
 * labels, same rounding, WITH units. Until now the projection was in no key
 * list, so its ten gal/day figures were invisible to every audit gate —
 * WATER-TRUTH-1 enforced screen bands beside an unenforced table on the same
 * page, and PPTX / DOCX carried it unchecked. Each water row names itself a
 * modeled scenario duty so the guard reads it as what it is; the guard admits
 * these exact figures as its own allowed values (`bandsFromGenerationProjection`).
 *
 * P3.3 follow-through (2026-09-05) — every other block the page shows is
 * rendered too, with the same labels and units: capacity & resilience, the
 * capacity screen (go/no-go summary), screening economics, operating
 * emissions, the noise & lighting screen, the max-power envelope and the
 * configurations screened before promotion. Each compared configuration's
 * peak water is labelled a screened-configuration duty and admitted by the
 * guard the same way, so the reader sees nothing the audit did not.
 */
export function renderGenerationImpactProjection(
  projection: GenerationImpactReportProjection | null | undefined,
): string {
  if (!projection) return '';
  const blocks: string[] = [];
  const water = projection.waterResource;
  if (water) {
    const rows: Array<[string, number | null]> = [
      ['Generation withdrawal', water.generationWithdrawalGalPerDay],
      ['Generation consumption', water.generationConsumptionGalPerDay],
      ['CCS withdrawal', water.ccsWithdrawalGalPerDay],
      ['CCS consumption', water.ccsConsumptionGalPerDay],
      ['Data-center withdrawal', water.dataCenterWithdrawalGalPerDay],
      ['Data-center consumption', water.dataCenterConsumptionGalPerDay],
      ['Total withdrawal', water.totalWithdrawalGalPerDay],
      ['Total consumption', water.totalConsumptionGalPerDay],
      ['Documented available capacity', water.availableConsumptionGalPerDay],
      ['Consumption headroom', water.consumptionHeadroomGalPerDay],
    ];
    blocks.push([
      '### Promoted scenario · Water duty (modeled scenario duty, gal/day)',
      ...rows.map(([label, value]) => (
        value == null || !Number.isFinite(value)
          ? `${label} (modeled scenario duty): Indeterminate`
          : `${label} (modeled scenario duty): ${fmtInt(value)} gal/day`
      )),
      `Water adequacy: ${engineeringVerdictLabel(water.adequacy)}`,
      ...(water.basis ? [water.basis.statement] : []),
    ].join('\n'));
  }
  const fuel = projection.fuelSupply;
  if (fuel) {
    const binding = (limit: 'station-design' | 'contract-mdq' | null): string =>
      limit === 'contract-mdq' ? 'Contract binds' : limit === 'station-design' ? 'Station binds' : 'Indeterminate';
    const pressure = (value: number | null, reference: 'gauge' | 'absolute' | null): string =>
      value == null ? 'Not documented' : `${fmtInt(value)} kPa ${reference ?? ''}`.trim();
    blocks.push([
      '### Promoted scenario · Fuel-gas interconnect (Dth/day)',
      fuel.quantityBasis === 'firm-transport-agreement'
        ? 'Basis: firm transport agreement'
        : 'Basis: indicative · scoping class, not firm transport',
      `Calculated peak nomination: ${fmtInt(fuel.peakGasDthPerDay)} Dth/day`,
      `Station design capacity: ${fmtInt(fuel.stationDthPerDay)} Dth/day`,
      `Contract maximum daily quantity: ${fmtInt(fuel.contractDthPerDay)} Dth/day`,
      `Deliverable at rated pressure (${binding(fuel.firmBindingLimit)}): ${fmtInt(fuel.firmDeliverableDthPerDay)} Dth/day`,
      `Headroom at rated pressure: ${fmtInt(fuel.headroomAtRatedPressureDthPerDay)} Dth/day`,
      `Deliverable at minimum delivery pressure (${binding(fuel.deratedBindingLimit)}): ${fmtInt(fuel.deratedDeliverableDthPerDay)} Dth/day`,
      `Headroom at minimum delivery pressure: ${fmtInt(fuel.headroomAtMinimumPressureDthPerDay)} Dth/day`,
      `Capacity rated at: ${pressure(fuel.ratedAtPressureKPa, fuel.ratedAtPressureReference)}`,
      `Guaranteed minimum delivery pressure: ${pressure(fuel.documentedMinimumDeliveryPressureKPa, fuel.documentedMinimumDeliveryPressureReference)}`,
      `Derate factor applied: ${fuel.absolutePressureRatio == null ? 'Not established' : `${fmtPct1(fuel.absolutePressureRatio * 100)}%`}`,
      `Compressibility basis: ${fuel.compressibilityBasis === 'documented' ? 'Documented real-gas Z' : `Ideal gas · ±${fmtInt(fuel.uncertaintyDthPerDay)} Dth/day`}`,
      `Fuel-supply adequacy: ${engineeringVerdictLabel(fuel.adequacy)}`,
      ...(fuel.assumptions.length > 0 ? [`Screening assumptions: ${fuel.assumptions.join(' ')}`] : []),
    ].join('\n'));
  }
  const footprint = projection.testFitFootprint;
  if (footprint) {
    blocks.push([
      '### Promoted scenario · Generation footprint (acres)',
      `Plant: ${fmtInt(footprint.plantAcres)} acres`,
      `Cooling: ${fmtInt(footprint.coolingAcres)} acres`,
      `Switchyard: ${fmtInt(footprint.switchyardAcres)} acres`,
      `CCS: ${fmtInt(footprint.ccsAcres)} acres`,
      `Compression: ${fmtInt(footprint.compressionAcres)} acres`,
      `Total: ${fmtInt(footprint.totalAcres)} acres`,
      ...(footprint.parcelAreaAcres != null ? [`Parcel area: ${fmtInt(footprint.parcelAreaAcres)} acres`] : []),
    ].join('\n'));
  }
  blocks.push(...renderGenerationImpactScreeningBlocks(projection));
  return blocks.join('\n\n');
}

const fmtDec = (value: number | null | undefined, digits: number): string =>
  value == null || !Number.isFinite(value)
    ? 'Indeterminate'
    : value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const screenVerdictLabel = (verdict: string | undefined): string =>
  verdict === 'pass' ? 'Pass' : verdict === 'conditional' ? 'Conditional' : verdict === 'fail' ? 'Fail' : 'Indeterminate';
const cfLabel = (value: number | null): string =>
  value == null || !Number.isFinite(value) ? 'indeterminate CF' : `${Math.round(value * 100)}% CF`;

/**
 * The blocks beyond the three duty tables — rendered in the page's own order
 * (capacity, capacity screen, economics, emissions, noise & lighting,
 * max-power envelope, screened configurations), labels as the page shows them.
 */
function renderGenerationImpactScreeningBlocks(projection: GenerationImpactReportProjection): string[] {
  const blocks: string[] = [];
  const capacity = projection.capacityAndResilience;
  if (capacity) {
    blocks.push([
      '### Promoted scenario · Capacity & resilience (MW)',
      `Normal net output: ${fmtInt(capacity.normalNetMW)} MW (operating basis)`,
      `Facility load: ${fmtInt(capacity.facilityLoadMW)} MW (user basis)`,
      `Critical load: ${fmtInt(capacity.criticalLoadMW)} MW (user basis)`,
      `${capacity.criterion} net after ${fmtInt(capacity.contingencyLostMW)} MW loss: ${fmtInt(capacity.contingencyNetMW)} MW — ${engineeringVerdictLabel(capacity.loadVerdict)}`,
      `${capacity.criterion} headroom vs critical load: ${fmtInt(capacity.loadMarginMW)} MW — ${engineeringVerdictLabel(capacity.loadVerdict)}`,
      `${capacity.criterion} headroom vs reserve requirement (${fmtPct1(capacity.reserveMarginPct)}% = ${fmtInt(capacity.reserveRequiredMW)} MW): ${fmtInt(capacity.reserveMarginMW)} MW — ${engineeringVerdictLabel(capacity.reserveVerdict)}`,
      `Probability load served: ${capacity.probabilityLoadServed == null ? 'not calculated' : `${(capacity.probabilityLoadServed * 100).toFixed(2)}%`}. ${capacity.reliabilityCaveat}`,
      ...(capacity.screenVerdict && capacity.screenStatement
        ? [`Capacity screen: ${screenVerdictLabel(capacity.screenVerdict)} — ${capacity.screenStatement}`]
        : []),
      ...(capacity.qualificationNote ? [capacity.qualificationNote] : []),
    ].join('\n'));
  }
  const goNoGo = projection.goNoGoSummary;
  if (goNoGo) {
    blocks.push([
      '### Promoted generation scenario · Capacity screen',
      `Capacity verdict: ${engineeringVerdictLabel(goNoGo.capacityVerdict)}${goNoGo.capacityScreenVerdict ? ` (screen: ${screenVerdictLabel(goNoGo.capacityScreenVerdict)})` : ''}`,
      goNoGo.summary,
      ...goNoGo.risks.map((risk) => `Risk (${risk.severity}): ${risk.message}`),
      ...goNoGo.gaps.map((gap) => `Data gap: ${gap}`),
      ...goNoGo.warnings.map((warning) => `Planning warning: ${warning}`),
    ].join('\n'));
  }
  const economics = projection.screeningEconomics;
  if (economics) {
    blocks.push([
      '### Promoted scenario · Screening economics (indicative)',
      ...economics.rows.map((row) => (
        `${row.label}: $${fmtInt(row.lowUsdPerKw)}–$${fmtInt(row.highUsdPerKw)}/kW installed; `
        + `$${fmtDec(row.servedUsdPerMwhLow, 1)}–$${fmtDec(row.servedUsdPerMwhHigh, 1)}/MWh at ${cfLabel(economics.servedCapacityFactor)} (served); `
        + `$${fmtDec(row.capabilityUsdPerMwhLow, 1)}–$${fmtDec(row.capabilityUsdPerMwhHigh, 1)}/MWh at ${cfLabel(economics.capabilityCapacityFactor)} (capability)`
      )),
      ...(economics.capture
        ? [`45Q at $${economics.capture.creditUsdPerTonne}/t on ${economics.capture.servedCaptureTonnesPerYear == null
          ? 'an indeterminate served capture volume'
          : `~${(economics.capture.servedCaptureTonnesPerYear / 1e6).toFixed(1)} Mt/yr served capture`}`
          + `${economics.capture.servedCreditMUsdPerYear == null ? '' : ` (~$${economics.capture.servedCreditMUsdPerYear.toFixed(0)}M/yr)`}`
          + ` vs a $${economics.capture.captureCostUsdPerTonneLow}–$${economics.capture.captureCostUsdPerTonneHigh}/t capture-cost archetype. ${economics.capture.note}`]
        : []),
      `Fuel $${economics.fuelUsdPerMwhNet.toFixed(1)}/MWh net at $${economics.fuelPriceUsdPerMmbtu.toFixed(2)}/MMBtu (${economics.fuelPriceSource}) · O&M $${economics.omUsdPerMwh}/MWh · FCR ${Math.round(economics.fixedChargeRate * 100)}%. ${economics.note}`,
    ].join('\n'));
  }
  const environment = projection.environmentalAndCommunity;
  if (environment) {
    blocks.push([
      '### Promoted scenario · Operating emissions (tonnes/year)',
      `Uncontrolled CO2: ${fmtInt(environment.uncontrolledCo2TonnesPerYear)} t/yr`,
      `Captured CO2: ${fmtInt(environment.capturedCo2TonnesPerYear)} t/yr`,
      `Residual CO2: ${fmtInt(environment.residualCo2TonnesPerYear)} t/yr`,
      `NOx: ${fmtDec(environment.noxTonnesPerYear, 1)} t/yr`,
      `CO: ${fmtDec(environment.coTonnesPerYear, 1)} t/yr`,
    ].join('\n'));
    blocks.push([
      '### Promoted scenario · Noise & lighting screen',
      ...(environment.noise.length > 0
        ? environment.noise.map((row) => (
          `${row.label}: ${row.predictedDbA == null ? 'Indeterminate' : `${row.predictedDbA.toFixed(1)} dBA`} predicted vs `
          + `${row.thresholdDbA == null ? 'no threshold' : `${row.thresholdDbA.toFixed(1)} dBA threshold`}`
          + `${row.distanceM == null ? '' : ` at ${fmtInt(row.distanceM)} m`} — ${engineeringVerdictLabel(row.verdict)}. ${row.caveat}`
        ))
        : ['No receptor noise result was determined for the promoted scenario.']),
      `Lighting load: ${fmtDec(environment.lighting.installedPowerKW, 1)} kW · installed output: ${fmtInt(environment.lighting.installedLumens)} lumens.`,
      `Controls: fully shielded ${environment.lighting.fullyShielded == null ? 'not documented' : environment.lighting.fullyShielded ? 'yes' : 'no'}`
        + ` · BUG-rated ${environment.lighting.bugRated == null ? 'not documented' : environment.lighting.bugRated ? 'yes' : 'no'}`
        + ` · motion/curfew controls ${environment.lighting.motionOrCurfewControls == null ? 'not documented' : environment.lighting.motionOrCurfewControls ? 'yes' : 'no'}`
        + ` · IES photometric file ${environment.lighting.iesPhotometricAvailable == null ? 'not documented' : environment.lighting.iesPhotometricAvailable ? 'available' : 'not available'}.`,
      ...environment.lighting.planningFlags.map((flag) => `Lighting planning flag: ${flag}`),
    ].join('\n'));
  }
  if (projection.maxPowerWithheldReason) blocks.push(`### Max-power study withheld\n${projection.maxPowerWithheldReason}`);
  const envelope = projection.maxPowerEnvelope;
  if (envelope) {
    const q = envelope.qualifier;
    blocks.push([
      '### Promoted scenario · Max-power envelope',
      `Maximum firm generation: ${q}${fmtInt(envelope.firmNetMW)} MW (N-k firm · ${envelope.blockCount} × ${fmtInt(envelope.isoGrossMWPerBlock)} MW blocks)`,
      `Installed ISO nameplate: ${q}${fmtInt(envelope.isoGrossNameplateMW)} MW (before contingency)`,
      `Supportable facility load: ${q}${fmtInt(envelope.facilityMW)} MW (after ${envelope.tdLossPct}% T&D)`,
      `At generation terminals: ${q}${fmtInt(envelope.facilityMWAtGenerationTerminals)} MW (behind-the-meter reading)`,
      `Supportable IT load: ${q}${fmtInt(envelope.itLoadMW)} MW (PUE ${envelope.pue}, ${envelope.pueSource})`,
      `Apparent power: ${q}${fmtInt(envelope.apparentMVA)} MVA (transformer / switchgear basis)`,
      envelope.bindingStatement,
      ...envelope.ceilings.map((ceiling) => (
        `Ceiling — ${ceiling.label}: ${ceiling.ceilingSiteGrossMW == null ? 'not binding / not established' : `${fmtInt(ceiling.ceilingSiteGrossMW)} MW site gross`}`
        + `${ceiling.limitValue != null && ceiling.limitUnit ? ` (limit ${fmtDec(ceiling.limitValue, 1)} ${ceiling.limitUnit})` : ''} — ${ceiling.status}${ceiling.reason ? `; ${ceiling.reason}` : ''}`
      )),
      ...envelope.caveats.map((caveat) => `Caveat: ${caveat}`),
    ].join('\n'));
  }
  const comparison = projection.comparison;
  if (comparison) {
    blocks.push([
      '### Configurations screened before promotion',
      comparison.statement,
      ...comparison.rows.map((row) => (
        `${row.scenarioName}${row.promoted ? ' (promoted)' : ' (screened, not promoted)'} — ${row.equipmentBasis}; `
        + `${row.criterionLabel} net ${fmtInt(row.criterionNetMW)} MW; reserve margin ${fmtInt(row.reserveMarginMW)} MW; `
        + `peak gas ${fmtInt(row.peakGasMmbtuPerHour)} MMBtu/h; `
        + `peak water (screened-configuration duty): ${row.peakWaterConsumptionGalPerDay == null ? 'Indeterminate' : `${fmtInt(row.peakWaterConsumptionGalPerDay)} gal/day`}; `
        + `footprint ${fmtDec(row.footprintTotalAcres, 2)} acres`
      )),
      ...(comparison.unresolvedScenarioIds.length > 0
        ? [`${comparison.unresolvedScenarioIds.length} compared configuration${comparison.unresolvedScenarioIds.length === 1 ? '' : 's'} could not be resolved.`]
        : []),
    ].join('\n'));
  }
  return blocks;
}

export const EXTRACT_KNOWN_STRING_SECTIONS = [
  'waterScreening', 'executiveSummary', 'summary', 'sitingNotes', 'developmentPathway', 'projectIntentOverview',
  'executive', 'stakeholderMap', 'perBtmSentiment', 'themeDeepDives', 'trendAnalysis',
  'comparableSiteBenchmark', 'mitigationPlaybook', 'sentimentAppendix', 'appendix',
  'constructabilityAssessment', 'costRangeEstimate', 'maxCampusCapacity', 'environmental',
  // test-fit
  'topography', 'parcelOverview', 'buildingPlacement', 'setbackCompliance', 'parkingAccess',
  'stormwater', 'utilityRouting', 'phasedBuildout', 'maxCapacity',
  // environmental
  'nepaScreening', 'ecologicalAssessment', 'waterResources', 'airQualityEmissions',
  'floodNaturalHazards', 'culturalHistorical', 'landUseZoning', 'noiseVisualImpact', 'mitigationPlan', 'alternativesAnalysis',
  'cumulativeImpacts', 'publicParticipationPlan', 'permittingPathway', 'environmentalRiskMatrix',
  // power-availability
  'gridCapacity', 'interconnectionQueue', 'rateStructures', 'curtailmentRisk', 'renewableIntegration',
  'gridRedundancy', 'powerStrategyContext',
  // water-resource
  'waterRights', 'supplyReliability', 'regulatoryPermits', 'droughtResilience', 'groundwater',
  'coolingByTech', 'wastewaterStormwater',
  // custom-brief
  'customBriefFindings', 'customBriefEvidence', 'customBriefRedFlags', 'customBriefConfidence',
  'customBriefRiskSynthesis', 'customBriefQuestionCoverage',
  // supply-chain-risk + executive-summary/financial bodies — previously
  // dropped (not in any known list), so the judge + distribution checker saw
  // these report types as near-empty. `conclusion` also moved here because the
  // executive-summary/financial schema makes it a STRING (the object-only loop
  // below skipped it); this loop handles string AND object AND array shapes.
  'supplyChainAnalysis', 'btmViabilityAssessment', 'conclusion',
] as const;
export const EXTRACT_KNOWN_OBJECT_SECTIONS = [
  'gridStudy', 'transportStudy', 'connectivityStudy', 'climateSevereWeather',
  'infrastructureInterconnection', 'insights', 'projectContext', 'powerAnalysis', 'interconnectionAnalysis',
  // `waterAnalysis` and `testFitAnalysis` were the last two typed containers
  // still unread. The `// water-resource` and `// test-fit` groups above list
  // SECTION IDS (`waterRights`, `parcelOverview`), but on a typed body the
  // prose lives at `waterAnalysis.waterRightsAnalysis` /
  // `testFitAnalysis.siteConstraints` — so those entries matched nothing and
  // the detectors saw both report types as little more than their executive
  // summary. Same id-vs-data-key gap `supplyChainAnalysis` was added for.
  'waterAnalysis', 'testFitAnalysis',
] as const;
export const EXTRACT_KNOWN_ARRAY_SECTIONS = [
  'keyFindings', 'risks', 'recommendations', 'criticalRisks', 'recommendedNextSteps',
  // R-72 STEP 4 — substantive list-valued narrative fields that were dropped,
  // under-counting words/citations on every typed report.
  'structuredFindings', 'contextualKeyFindings', 'developmentRisks', 'claims',
] as const;

export function extractSections(report: SiteRagReport): Array<{ id: string; content: string }> {
  const out: Array<{ id: string; content: string }> = [];
  const r = report as unknown as Record<string, unknown>;

  // Domain sections arrive as STRING (legacy) OR OBJECT (typed reports) OR
  // ARRAY. The prior code only captured the string case, dropping the
  // object-valued typed bodies (nepaScreening, gridCapacity, …) — the dominant
  // cause of the gate's word/citation under-count. Capture all three shapes;
  // objects render with `### {subKey}` sub-headings (real subsection structure).
  for (const k of EXTRACT_KNOWN_STRING_SECTIONS) {
    const v = r[k];
    if (typeof v === 'string') {
      if (v.trim()) out.push({ id: k, content: v });
    } else if (Array.isArray(v)) {
      const c = flattenToText(v).trim();
      if (c) out.push({ id: k, content: c });
    } else if (v && typeof v === 'object') {
      const c = renderObjectWithSubheadings(v as Record<string, unknown>).trim();
      if (c) out.push({ id: k, content: c });
    }
  }
  for (const k of EXTRACT_KNOWN_OBJECT_SECTIONS) {
    if (r[k] && typeof r[k] === 'object' && !Array.isArray(r[k])) {
      const c = renderObjectWithSubheadings(r[k] as Record<string, unknown>).trim();
      if (c) out.push({ id: k, content: c });
    } else if (Array.isArray(r[k])) {
      const c = flattenToText(r[k]).trim();
      if (c) out.push({ id: k, content: c });
    }
  }
  for (const k of EXTRACT_KNOWN_ARRAY_SECTIONS) {
    if (Array.isArray(r[k])) out.push({ id: k, content: flattenToText(r[k]) });
  }
  for (const ans of report.customBriefAnswers ?? []) {
    out.push({
      id: `customBriefQ_${ans.questionId}`,
      content: [ans.question, ans.findings, ans.evidence].filter(Boolean).join('\n'),
    });
  }
  // P3.3 — the frozen promoted-scenario duty tables, so the gates that
  // enforce screen bands also see the table beside them (see the renderer).
  const projectionText = renderGenerationImpactProjection(report.generationImpactProjection);
  if (projectionText.trim()) out.push({ id: 'generationImpactProjection', content: projectionText });
  // C0-GEN Wave 2 (spec fix #1) — append the generated editorial prose so the
  // audit gates measure + verify it. Empty string on flag-OFF / legacy reports
  // ⇒ no section pushed ⇒ byte-identical to today.
  const editorial = flattenEditorialNarrative(report);
  if (editorial.trim()) out.push({ id: 'editorialNarrative', content: editorial });
  return out;
}

/** Legacy markdown remains authoritative for its own prose, while a newly
 * assembled deterministic water section is an additional reader-visible part. */
function markdownWithWaterScreening(markdown: string, report: SiteRagReport): string {
  const water = report.waterScreening?.trim();
  return water && !markdown.includes(water)
    ? `${markdown}\n\n## Modeled Water Use\n\n${water}`
    : markdown;
}

export function reportFullText(report: SiteRagReport): string {
  const md = (report as SiteRagReport & { markdown?: string }).markdown?.trim() ?? '';
  const sections = extractSections(report).map((s) => `# ${s.id}\n${s.content}`).join('\n\n');
  if (md.length > 100) return markdownWithWaterScreening(md, report);
  if (md.length > 0) return sections.length > 0 ? `${md}\n\n${sections}` : md;
  return sections;
}

/**
 * R-72 — render the report as the MARKDOWN BODY a reader actually sees, so
 * distribution-sanity stats (headings, words, citations) are measured on real
 * rendered prose rather than the `# <id>\n<dump>` key:value serialization
 * produced by {@link reportFullText}.
 *
 * The viewer ({@link NewTypeSectionsRouter}) emits an `<h2>{label}</h2>` per
 * ordered section followed by the section's narrative body. We mirror that:
 * each section becomes a `## {resolvedLabel}` H2 heading (matching the reader's
 * H2) followed by the section prose. Inline `##`/`###` sub-headings, `[Ref N]`
 * citations, tables, and figures already embedded in the section prose are
 * preserved verbatim.
 *
 * Section ordering follows `report.sectionOrder` when present (so the body
 * matches the rendered order); otherwise extraction order is used. A
 * pre-rendered `report.markdown` body (>100 chars) retains its prose; a stored
 * deterministic water screen is appended as an additional visible section.
 */
export function renderReportMarkdownBody(report: SiteRagReport): string {
  const md = (report as SiteRagReport & { markdown?: string }).markdown?.trim() ?? '';
  if (md.length > 100) return markdownWithWaterScreening(md, report);

  const reportType = (report.reportType ?? 'feasibility') as ReportTypeId;
  const sections = extractSections(report);
  if (sections.length === 0) return md;

  // Order sections by the rendered sectionOrder when available so the measured
  // body matches what the reader scrolls through; unordered sections (and any
  // not named in sectionOrder) keep extraction order, appended after.
  const order = report.sectionOrder ?? [];
  const orderRank = new Map<string, number>(order.map((id, i) => [id, i]));
  const ranked = sections
    .map((s, i) => ({ s, i }))
    .sort((a, b) => {
      const ra = orderRank.has(a.s.id) ? orderRank.get(a.s.id)! : Number.MAX_SAFE_INTEGER;
      const rb = orderRank.has(b.s.id) ? orderRank.get(b.s.id)! : Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      return a.i - b.i; // stable: preserve extraction order within equal rank
    })
    .map((x) => x.s);

  const blocks: string[] = [];
  for (const { id, content } of ranked) {
    const body = content.trim();
    if (body.length === 0) continue;
    const label = getSectionLabel(id, reportType);
    // `## {label}` mirrors the renderer's per-section <h2>. The section's own
    // prose may already open with its own `##`/`###` markdown heading; that is
    // intentionally preserved (it is part of the rendered body).
    blocks.push(`## ${label}\n\n${body}`);
  }

  const rendered = blocks.join('\n\n');
  // Defensive: if every section was empty, fall back to whatever markdown we had.
  return rendered.length > 0 ? rendered : md;
}

/**
 * Every ADDRESSABLE, POPULATED field path the report carries, as `parent.child`.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * Citations address report content by FIELD PATH (`conclusion.topRisks`,
 * `waterAnalysis.surfaceWaterAssessment`) while `renderedSections` is addressed
 * by SECTION ID (`conclusion`, `supplyAvailability`). The alias bridge in the
 * gate's caller covers only sections that have a `fieldPathForSection` mapping —
 * `conclusion` has none, so nothing citing `conclusion.*` could ever resolve.
 *
 * Live (Redhead, 2026-08-16): 5 BLOCK-severity dead pointers across 2 reports,
 * every one naming a field that exists and is populated —
 * `waterAnalysis.surfaceWaterAssessment` held 734 characters of rendered prose.
 * A dead-pointer block is meant to mean "this citation leads the reader
 * nowhere"; here the content was right there.
 *
 * ── Why "populated" is the right test ───────────────────────────────────────
 * The gate protects a READER following a citation. If the path holds content,
 * the reader finds it, so the citation is not dead — regardless of which section
 * id happens to render it. Empty and whitespace-only fields are deliberately
 * EXCLUDED: citing a field that exists but says nothing IS a dead pointer, and
 * that catch is preserved.
 *
 * DEPTH 3, not 2 (corrected 2026-08-16 by adversarial review). The optional
 * STRUCTURED GATE objects nest one level deeper and each leaf renders
 * independently: `waterAnalysis.consumptiveUse` is a `ConsumptiveUseGate` whose
 * `dryCoolingPueTradeoff` leaf renders verbatim in CoolingByTechSection
 * (siteRag.ts:193, pinned by WaterResourceSections.test.tsx:192), and
 * `supplyChainAnalysis.longLeadVsCod` follows the same pattern. A depth-2 cap
 * registered only the PARENT, so a citation naming the leaf still reported a
 * BLOCK dead pointer against prose the reader can plainly see — the very
 * unfixable-hold class this function exists to close.
 *
 * `isPopulated` is RECURSIVE for the same reason: an object counts as populated
 * only if some leaf under it actually holds content. Counting keys instead meant
 * `{ doctrineGate: '', withdrawalPermit: '' }` registered an addressable path
 * with nothing in it, turning a true dead pointer into a silent miss.
 */
/**
 * Top-level keys that are GENERATION MACHINERY, never reader-visible content.
 *
 * Measured on a live water-resource body: of 493 addressable paths, only ~10 were
 * rendered prose. `datasets` contributed 89, `qaFindings` 52 (the gate's own
 * findings), `projectIntentAtGen` 49. Registering those as citable means a
 * citation naming one RESOLVES, which is a silent miss in a gate whose entire
 * job is proving a citation leads somewhere a reader can go.
 *
 * DENY, never ALLOW: the error costs are asymmetric. Wrongly denying a rendered
 * root re-manufactures a dead-pointer block — an unfixable hold, the expensive
 * failure. Wrongly permitting machinery only misses a catch no live citation has
 * ever attempted. So this lists only keys whose names encode their internal
 * nature (`*AtGen` / `*Snapshot` suffix families, telemetry, provenance,
 * revisions, audits) and leaves every ambiguous root addressable.
 */
const NON_RENDERED_ROOT_RE =
  /^(?:datasets|qaFindings|claims|references|documents|documentIds|documentRefs|sectionVisibility|sectionProvenance|analysisReadiness|analysisRevision|reportRevision|citationAudit|citationDensity|generationTelemetry|scoringInfluence|coverageManifestAtGen|crossValidationFindings|.*AtGen|.*Snapshot)$/;

export function populatedFieldPaths(report: SiteRagReport, maxDepth = 3): string[] {
  const out: string[] = [];
  const r = report as unknown as Record<string, unknown>;

  const isPopulated = (v: unknown): boolean => {
    if (typeof v === 'string') return v.trim().length > 0;
    if (typeof v === 'number' || typeof v === 'boolean') return true;
    if (Array.isArray(v)) return v.some(isPopulated);
    if (v && typeof v === 'object') return Object.values(v as object).some(isPopulated);
    return false;
  };

  const walk = (node: Record<string, unknown>, prefix: string, depth: number): void => {
    for (const [key, value] of Object.entries(node)) {
      if (!isPopulated(value)) continue;
      const path = `${prefix}.${key}`;
      out.push(path);
      if (depth < maxDepth && value && typeof value === 'object' && !Array.isArray(value)) {
        walk(value as Record<string, unknown>, path, depth + 1);
      }
    }
  };

  for (const [parent, value] of Object.entries(r)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    if (NON_RENDERED_ROOT_RE.test(parent)) continue;
    walk(value as Record<string, unknown>, parent, 2);
  }
  return out;
}

/**
 * Every id a citation may legitimately name: each populated field path AND its
 * bare LEAF segment.
 *
 * Citations address content BOTH ways. Measured on the first live v1.25.0 run:
 * `§criticalPathAssessment`, `§supplierStrategy`, `§supplyChainRiskSynthesis`,
 * `§quoteComparisonInsights` and `§btmDecisionSummary` were emitted as bare leaf
 * names, and 5 BLOCK-severity dead pointers landed on fields that were present
 * and populated under `supplyChainAnalysis.*` / `conclusion.*`. Registering only
 * `parent.child` left that half of the namespace unreachable.
 *
 * A leaf is registered ONLY because some populated path ends in it, so this
 * cannot launder a genuine miss — a report that does not carry the field
 * registers nothing for it. Live proof from the same run: a public-sentiment
 * report citing `§btmDecisionSummary`, which it does not carry, stays dead.
 */
export function addressableCitationIds(report: SiteRagReport, maxDepth = 3): string[] {
  const out = new Set<string>();
  for (const path of populatedFieldPaths(report, maxDepth)) {
    out.add(path);
    const leaf = path.slice(path.lastIndexOf('.') + 1);
    if (leaf) out.add(leaf);
  }
  return [...out];
}
